import { useTheme } from '@itsrever/design-system'
import BankTransfer from '@/ui/components/portal-preview/pages/refund-methods/assets/bank-transfer.svg'
import OPM from '@/ui/components/portal-preview/pages/refund-methods/assets/opm.svg'
import { usePortalTranslate } from '@/hooks'
import { useContext } from 'react'
import { EditedCopiesContext } from '@/ui/components/portal-preview/preview-contexts'
import {
  findRefundMethodIndex,
  findRefundTimingIndex
} from '@/ui/components/portal-preview/settings-indexes'

interface RefundCardProps {
  method?: string
  timing?: string
  displayShippingTag?: boolean
}

export const RefundCard: React.FC<RefundCardProps> = ({
  method,
  timing,
  displayShippingTag
}) => {
  const editedCopies = useContext(EditedCopiesContext)
  const { translate } = usePortalTranslate(editedCopies)

  const theme = useTheme()

  const instantMethod = method === 'GIFT_CARD' || method === 'PROMO_CODE'

  const tagBackgroundColor = instantMethod ? '#1b75eb' : theme.colors.grey[0]

  const iconSource = method === 'BANK_TRANSFER' ? BankTransfer : OPM

  const methodIndex = findRefundMethodIndex(method ?? '')
  const timingIndex = findRefundTimingIndex(timing ?? '')

  return (
    <div
      id={method?.toString()}
      className="relative mb-6 flex content-center items-center rounded-lg border border-solid border-[#ccc] shadow-md"
    >
      {instantMethod && displayShippingTag ? (
        <div
          className="absolute left-[16px] top-[-10px] rounded-[20px] border border-solid border-[#1b75eb] px-[0.8rem] py-[0.1rem] text-white"
          style={{
            background:
              'radial-gradient(circle at 78px 10px, #0076e7, #07a5fe 100%)'
          }}
        >
          <p className="text-xs">
            <b
              dangerouslySetInnerHTML={{
                __html: translate('refund_card_free_shipping')
              }}
            />
          </p>
        </div>
      ) : null}
      <div className="w-full cursor-pointer rounded px-[12px] py-[14px]">
        <div className="flex items-center gap-2">
          {instantMethod ? (
            <svg
              width="15"
              height="15"
              viewBox="0 0 15 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="z-[100] size-[15px] overflow-visible"
            >
              <path
                id="TOP"
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M14.5714 6.881V4.85719C14.5714 4.41011 14.209 4.04767 13.7619 4.04767H9.27714L11.819 2.10481C11.9908 1.97599 12.1043 1.78421 12.1347 1.57167C12.1651 1.35913 12.1097 1.14323 11.981 0.971475L11.4952 0.323856C11.227 -0.0338126 10.7196 -0.1063 10.3619 0.161951L7.28571 2.53386L4.20952 0.161951C3.85185 -0.1063 3.34444 -0.0338126 3.07619 0.323856L2.59048 0.971475C2.32222 1.32915 2.39471 1.83656 2.75238 2.10481L5.29429 4.04767H0.809524C0.36244 4.04767 0 4.41011 0 4.85719V6.881H14.5714Z"
                fill="#5E5E63"
              />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M0.817619 9.30957H6.07952L6.07143 14.5715H1.61905C1.17196 14.5715 0.809524 14.2091 0.809524 13.762L0.817619 9.30957ZM13.7619 9.30957V13.762C13.7619 14.2091 13.3995 14.5715 12.9524 14.5715H8.5V9.30957H13.7619Z"
                fill="#5E5E63"
              />
            </svg>
          ) : (
            <img className="size-[15px]" src={iconSource} />
          )}
          <p className="text-lg">
            <b
              dangerouslySetInnerHTML={{
                __html: translate(`refund_card_method${methodIndex}_title`)
              }}
            />
          </p>
        </div>

        <p
          className="mt-2 font-normal"
          style={{
            color: theme.colors.grey[0]
          }}
          dangerouslySetInnerHTML={{
            __html: translate(
              `refund_card_method${methodIndex}_description${timingIndex}`
            )
          }}
        />
        {timing === 'ON_START_RETURN_PROCESS' && (
          <div className="flex justify-end">
            <div
              className="mr-[-12px] mt-2 w-[80px] origin-right animate-slideOutFromLeft whitespace-nowrap rounded-l-lg px-0 py-[3px] text-center"
              style={{ backgroundColor: tagBackgroundColor }}
            >
              <p
                className="text-[0.6rem] leading-[8px]"
                dangerouslySetInnerHTML={{
                  __html: translate(`refund_card_time_method${methodIndex}`)
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default RefundCard
